import React, { useState, useEffect, useRef, useCallback } from 'react';

import { Popover } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { 
  Character, 
  CharacterSheetInterface, 
  // CreatureHP, 
  CreatureInstance, 
  CustomParticipant, 
  RolledValue, 
  SummonInstance,
  TransformInstance
} from "@dndModels";

import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from '@utilities/common_utilities';

import HeartGrayFullBlackOutline from "../../../../assets/img/Heart_gray_full_black_outline.png";
import HeartGrayFullWhiteOutline from "../../../../assets/img/Heart_gray_full_white_outline.png";

import HeartLBlueFullBlackOutline from "../../../../assets/img/Heart_lblue_full_black_outline.png";
import HeartLBlueFullWhiteOutline from "../../../../assets/img/Heart_lblue_full_white_outline.png";
import HeartRedFullBlackOutline from "../../../../assets/img/Heart_red_full_black_outline.png";
import HeartRedFullWhiteOutline from "../../../../assets/img/Heart_red_full_white_outline.png";
import HeartYellowFullBlackOutline from "../../../../assets/img/Heart_yellow_full_black_outline.png";
import HeartYellowFullWhiteOutline from "../../../../assets/img/Heart_yellow_full_white_outline.png";

import StringBox from "@components/input/strings/StringBox";
import CheckBox from "@components/input/CheckBox";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import ButtonBox from "@components/input/buttons/ButtonBox";
import DandDButtonBox from "@dndComponents/DandDButtonBox";
import Roller from "@dndComponents/other/Roller";
import InfoButton from "@components/input/buttons/InfoButton";

interface Props {
  char: CharacterSheetInterface | CreatureInstance | SummonInstance | CustomParticipant;
  disabled?: boolean;
  view_only?: boolean;
  borderColor?: string;
}

const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

export default function HPInput({
  char,
  disabled = false,
  view_only,
  borderColor = "inherit",
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [open, setOpen] = useState(false);
  const [my_current, setMyCurrent] = useState(char.hp.current);
  const [my_max_mod, setMyMaxMod] = useState(char.hp.max_modifier);
  const [my_max_override, setMyMaxOverride] = useState(char.hp.override_max);
  const [my_temp, setMyTemp] = useState(char.hp.temp);
  const [change_hit_points, setChangeHitPoints] = useState(0);
  const [hp_change_key, setHPChangeKey] = useState(0);
  const [ping, setPing] = useState(false);
  const [roll, setRoll] = useState<RolledValue[]>([]);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLDivElement | null>(null);
  const [show_calcs, setShowCalcs] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const resourcesChanged = useCallback(() => {
    // console.log("HP");
    // console.log(character.hp);
    setMyCurrent(char.hp.current);
    setMyMaxMod(char.hp.max_modifier);
    setMyMaxOverride(char.hp.override_max);
    setMyTemp(char.hp.temp);
  }, [char, setMyCurrent, setMyMaxMod, setMyMaxOverride, setMyTemp]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      char.subscription_holder.subscribe("Resources", mounted, resourcesChanged);
    }
  }, [char, fetched, setFetched, resourcesChanged]);

  const renderExtras = () => {
    return (
      <ModalOrDrawer
        open={ open } 
        onClose={() => {
          setOpen(false);
        }}>
        { renderHPStuff() }
      </ModalOrDrawer>
    );
  }

  const saveChanges = () => {
    if (char instanceof Character) {
      char.changed();
      char.subscription_holder.resourcesChanged();
    } else if (char instanceof TransformInstance && char.character) {
      if (char.transform) {
        if (char.transform.current_hit_points.share) {
          char.character.hp.current = char.hp.current;
        }
        // if (character.transform.max_hit_points.share) {
        //   character.character.hp.max = character.hp.max;
        // }
        if (char.transform.temp_hit_points.share) {
          char.character.hp.temp = char.hp.temp;
        }
      }
      char.character.changed();
      char.character.subscription_holder.resourcesChanged();
    } else {
      char.subscription_holder.resourcesChanged();
    }
  }

  const applyDamage = (amount: number) => {
    if (my_temp > 0) {
      let new_hp = my_temp - amount;
      if (new_hp < 0) {
        setMyTemp(0);
        char.hp.temp = 0;
        new_hp += my_current;
        new_hp = char.hp.allow_negative ? new_hp : Math.max(new_hp, 0);
        setMyCurrent(new_hp);
        char.hp.current = new_hp;
      } else {
        setMyTemp(new_hp);
        char.hp.temp = new_hp;
      }
    } else if (my_current > 0) {
      const new_hp = char.hp.allow_negative ? my_current - amount : Math.max(my_current - amount, 0);
      setMyCurrent(new_hp);
      char.hp.current = new_hp;
    }
    saveChanges();
    setChangeHitPoints(0);
    setHPChangeKey(common_util.get_time_number());
  }

  const renderHPStuff = () => {
    const my_max = char.hp.max;
    const undying = false; // Some features give the ability to continue going when at 0 hp
    return (
      <Grid container spacing={0} direction="row"
        style={{ 
          overflowX: "hidden",
          padding: "10px",
          // fontWeight: "bold"
        }}>
        {/* <Grid
          className="centered"
          style={{
            fontSize: "20px"
          }}
          size={{
            xs: 12
          }}>
          { char.hp.called } Management
        </Grid> */}
        <Grid
          className="centered header"
          size={4}>
          Current { char.hp.called }
        </Grid>
        <Grid
          className="centered header"
          size={4}>
          Max { char.hp.called }
        </Grid>
        <Grid
          className="centered header"
          size={4}>
          Temp { char.hp.called }
        </Grid>
        <Grid size={4}>
          <StringBox 
            name=""
            value={`${my_current}`}
            type="number"
            // revert_key={revert_key}
            onBlur={(changed: string) => {
              let new_hp = +changed;
              if (new_hp < 0 && !char.hp.allow_negative) {
                new_hp = 0;
              } else {
                const true_max_hp = my_max_override === null ? (my_max + my_max_mod) : (my_max_override + my_max_mod);
                if (new_hp > true_max_hp) {
                  new_hp = true_max_hp;
                }
              }
              setMyCurrent(new_hp);
              char.hp.current = new_hp;
              saveChanges();
            }}
          />
        </Grid>
        <Grid
          className="centered"
          style={{
            fontSize: "20px"
          }}
          size={4}>
          { my_max }
          <InfoButton
            onClick={() => { setShowCalcs(!show_calcs); }}
          />
        </Grid>
        <Grid size={4}>
          <StringBox 
            name=""
            value={`${my_temp}`}
            type="number"
            onBlur={(changed: string) => {
              let new_hp = +changed;
              if (new_hp < 0) {
                new_hp = 0;
              }
              setMyTemp(new_hp);
              char.hp.temp = new_hp;
              saveChanges();
            }}
          />
        </Grid>
        <Grid
          className="centered"
          style={{ borderTop: "1px solid" }}
          container
          spacing={0}
          direction="row"
          size={4}>
          <Grid size={12} className="header">Change</Grid>
          <Grid size={12}>
            <StringBox 
              name=""
              value={`${change_hit_points}`}
              type="number"
              change_time={hp_change_key}
              // log_it
              // revert_key={revert_key}
              onBlur={(changed: string) => {
                setChangeHitPoints(+changed);
              }}
            />
          </Grid>
        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          style={{ borderTop: "1px solid" }}
          size={8}>
          <Grid size={6}>
            <ButtonBox
              name="Heal"
              style={{ color: "green", borderColor: "green" }}
              onClick={() => {
                const max_hp = my_max_override === null ? (my_max + my_max_mod) : (my_max_override + my_max_mod);
                const new_hp = Math.min(my_current + change_hit_points, max_hp);
                setMyCurrent(new_hp);
                char.hp.current = new_hp;
                saveChanges();
                setChangeHitPoints(0);
                setHPChangeKey(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid size={6}>
            <ButtonBox
              name="Damage"
              style={{ color: "red", borderColor: "red" }}
              onClick={() => {
                applyDamage(change_hit_points);
              }}
            />
          </Grid>
          <Grid size={12}>
            <ButtonBox
              name="Damage with Resistance"
              style={{ color: "red", borderColor: "red" }}
              onClick={() => {
                const change = Math.floor(change_hit_points / 2);
                applyDamage(change);
              }}
            />
          </Grid>
        </Grid>
        {/* <Grid item container spacing={0} direction="row" style={{ borderTop: "1px solid" }}> */}
        <Grid
          style={{ borderTop: "1px solid" }}
          className="centered header"
          size={6}>
          Max { char.hp.called } Modifier
        </Grid>
        <Grid
          style={{ borderTop: "1px solid" }}
          className="centered header"
          size={6}>
          Override Max { char.hp.called }
        </Grid>
        <Grid size={6}>
          <StringBox 
            name=""
            value={ my_max_mod === 0 ? "" : `${my_max_mod}` }
            type="number"
            onBlur={(changed: string) => {
              let new_mod = 0;
              if (changed === "") {
                new_mod = 0;
              } else {
                new_mod = +changed;
              }
              const true_max_hp = my_max_override === null ? (my_max + new_mod) : (my_max_override + new_mod);
              const new_current = my_current > true_max_hp ? true_max_hp : my_current;
              setMyMaxMod(new_mod);
              setMyCurrent(new_current);
              char.hp.current = new_current;
              char.hp.max_modifier = new_mod;
              saveChanges();
            }}
          />
        </Grid>
        <Grid size={2}>
          <CheckBox
            name=""
            value={my_max_override !== null}
            onChange={(changed: boolean) => {
              let new_hp: number | null = my_max + my_max_mod;
              if (!changed) {
                new_hp = null;
              }
              setMyMaxOverride(new_hp);
              char.hp.override_max = new_hp;
              saveChanges();
            }}
          />
        </Grid>
        <Grid size={4}>
          { my_max_override !== null && 
            <StringBox 
              name=""
              value={`${my_max_override}`}
              type="number"
              onBlur={(changed: string) => {
                const new_hp = +changed;
                if (my_current > new_hp) {
                  setMyCurrent(new_hp);
                  char.hp.current = new_hp;
                }
                setMyMaxOverride(new_hp);
                char.hp.override_max = new_hp;
                saveChanges();
              }}
            />
          }
        </Grid>
        { my_current === 0 && 
          my_temp === 0 && 
          !undying && renderDeathSaves()
        }
        { renderMultipliers(0) }
        { renderMultipliers(0.5) }
        { renderMultipliers(2) }
        { renderCalcs() }
      </Grid>
    );
  }

  const renderCalcs = () => {
    if (show_calcs) {
      return (
        <Grid
          size={{
            xs: 12
          }}>
          <Grid container spacing={0} direction="row">
            <Grid
              className='header'
              size={{
                xs: 12
              }}>Max HP Calculations</Grid>
            { char.hp.calc_steps.map((step, key) => {
              return (
                <Grid
                  key={key}
                  size={{
                    xs: 12
                  }}>
                  { step }
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      );
    }
    return null;
  }

  const renderMultipliers = (mult: number) => {
    if (char instanceof Character) {
      const multipliers = char.damage_multipliers.filter(o => o.multiplier === mult);
      if (multipliers.length > 0) {
        const label = mult === 0.5 ? "Resistance" : (mult === 0 ? "Immunity" : "Vulnerability");
        return (
          <>
            <Grid
              size={{
                xs: 12
              }}>
              <span className="header">{ label }:&nbsp;</span>
              { multipliers.map(o => o.damage_type).join(", ") }
            </Grid>
          </>
        );
      }
    } else if (char instanceof CreatureInstance || char instanceof SummonInstance) {
      const creature = char;
      switch (mult) {
        case 0:
          if (creature.immunities_string.length > 0) {
            return (
              <Grid size={12}>
                <strong>Immunities:</strong> {creature.immunities_string}
              </Grid>
            );
          }
        break;
        case 0.5:
          if (creature.resistances_string.length > 0) {
            return (
              <Grid size={12}>
                <strong>Resistances:</strong> {creature.resistances_string}
              </Grid>
            );
          }
        break;
        case 2:
          if (creature.vulnerabilities_string.length > 0) {
            return (
              <Grid size={12}>
                <strong>Vulnerabilities:</strong> {creature.vulnerabilities_string}
              </Grid>
            );
          }
        break;
      }
    }
    return null;
  }

  const renderDeathSaveExtras = () => {
    return (
      <>
        <Popover
          open={ popoverAnchorEl !== null }
          anchorEl={popoverAnchorEl}
          onClose={() => {
            setPopoverAnchorEl(null);
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          { renderRoll() }
        </Popover>
      </>
    );
  }

  const renderRoll = () => {
    if (popoverAnchorEl !== null) {
      return (
        <Roller 
          name="Death Save"
          rolled_values={roll} 
          type="Death Save" 
        />
      );
    }
    return null;
  }

  const renderDeathSaves = () => {
    const fails: any[] = [];
    const successes: any[] = [];
    for (let i = 0; i < 3; ++i) {
      fails.push(
        <CheckBox key={i}
          size={48}
          name=""
          value={char.hp.death_save_fails > i}
          onChange={(changed: boolean) => {
            let new_saves = char.hp.death_save_fails;
            if (changed) {
              new_saves++;
            } else {
              new_saves--;
            }
            char.hp.death_save_fails = new_saves;
            saveChanges();
            setPing(!ping);
          }}
        />
      );
      successes.push(
        <CheckBox key={i}
          size={48}
          name=""
          value={char.hp.death_save_successes > i}
          onChange={(changed: boolean) => {
            let new_saves = char.hp.death_save_successes;
            if (changed) {
              new_saves++;
            } else {
              new_saves--;
            }
            char.hp.death_save_successes = new_saves;
            saveChanges();
            setPing(!ping);
          }}
        />
      );
    }
    // eslint-disable-next-line
    const { advantage, advantages, greater_advantage } = char instanceof Character ? char_util.getAdvantage(char, "Saving Throw", "Death Saves") : { advantage: 0, advantages: [], greater_advantage: [] };
    return (
      <Grid container spacing={0} direction="row" style={{ borderTop: "1px solid" }}>
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>
          Death Saves
        </Grid>
        <Grid
          className="centered"
          size={{
            xs: 6
          }}>
          Failures
        </Grid>
        <Grid
          className="centered"
          size={{
            xs: 6
          }}>
          Successes
        </Grid>
        <Grid
          className="centered"
          size={{
            xs: 6
          }}>
          { fails }
        </Grid>
        <Grid
          className="centered"
          size={{
            xs: 6
          }}>
          { successes }
        </Grid>
        { char instanceof Character && 
          <Grid
            size={{
              xs: 12
            }}>
            <DandDButtonBox
              name={ `Make a Death Save` }
              advantage={advantage}
              onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                setRoll(char_util.rollD20({ char_interface: char, type: "Death Save", advantage }));
                setPopoverAnchorEl(event.currentTarget);
              }}
            />
            { renderDeathSaveExtras() }
          </Grid>
        }
      </Grid>
    );
  }

  const getHeartImage = () => {
    const max = char.hp.true_max;
    const current = my_current;
    // Blue = Full
    // Yellow = Injured but not bloodied
    // Red = Bloodied
    // Gray = Down
    const color = current === max ? "lblue" : 
      (current > 0.5 * max ? "yellow" : 
      (current > 0 ? "red" : "gray"));
    const outline = borderColor;
    switch(color) {
      case "gray": 
        if (outline === "white") {
          return HeartGrayFullWhiteOutline;
        } else {
          return HeartGrayFullBlackOutline;
        }
      case "lblue": 
        if (outline === "white") {
          return HeartLBlueFullWhiteOutline;
        } else {
          return HeartLBlueFullBlackOutline;
        }
      case "red": 
        if (outline === "white") {
          return HeartRedFullWhiteOutline;
        } else {
          return HeartRedFullBlackOutline;
        }
      case "yellow": 
        if (outline === "white") {
          return HeartYellowFullWhiteOutline;
        } else {
          return HeartYellowFullBlackOutline;
        }
    }
  }

  const renderCurrent = () => {
    const my_max = char.hp.max;
    // console.log(my_max);
    // console.log(my_current);
    return (
      <div style={{ float: "left", marginLeft: "4px", marginRight: "4px" }}>
        { my_current } 
        { my_temp > 0 && <span style={{ color: "blue" }}>+{my_temp}</span> } 
        &nbsp;/&nbsp;
        { my_max_override === null ? 
          my_max 
        : my_max_override > my_max ?
          <span style={{ color: "green" }}>{ my_max_override }</span> 
        : 
          <span style={{ color: "red" }}>{ my_max_override }</span> 
        } 
        { my_max_mod > 0 ? <span style={{ color: "green" }}>+{my_max_mod}</span> : (my_max_mod < 0 && <span style={{ color: "red" }}>{my_max_mod}</span>) }
      </div>
    );
  }

  // console.log(char.hp);
  if (disabled) {
    return (
      <img src={ getHeartImage() } alt="heart" 
        style={{
          width: "30px",
          height: "30px",
        }}
      />
    );
  } else {
    // const my_max = char.hp.max;
    const style = borderColor === "inherit" ? { border: `1px solid`} : { border: `1px solid ${borderColor}`};
    return (
      <>
        <div className={ view_only ? "" : "clickable" } style={style} 
          onClick={() => {
            if (!view_only) {
              setOpen(true);
            }
          }}>
          <div className="centered" style={{ flexDirection: "column" }}>
            <div style={{ 
              fontSize: "10px", 
              height: "16px", 
              margin: "1px",
            }}>
              Hit Points
            </div>
            <div className={ view_only ? "centered" : "centered clickable" } style={{
              margin: "1px", 
              fontSize: "16px"
            }}>
              { renderCurrent() }
            </div>
          </div>
        </div>
        { renderExtras() }
      </>
    );
  }
}
